import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Mail, Camera } from "lucide-react";
import Seo from "@/components/Seo";
import Breadcrumb from "@/components/Breadcrumb";
import { CATEGORIES, REALISATIONS } from "@/data/realisations";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const RealisationCard = ({ item, index }) => {
  const categories = CATEGORIES.filter((c) => item.categories.includes(c.id));
  return (
    <motion.article
      {...fadeUp}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      className="group border border-line bg-white"
      data-testid={`realisation-card-${item.id}`}
    >
      <div className="overflow-hidden">
        <img
          src={item.image}
          alt={item.alt}
          loading="lazy"
          className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="p-6">
        {categories.length > 0 && (
          <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-terrain">
            {categories.map((c) => c.label).join(" · ")}
          </p>
        )}
        <h3 className="mt-2 font-display text-lg font-bold tracking-tight text-midnight">
          {item.titre}
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-steel">{item.description}</p>
        {(item.localisation || item.date) && (
          <p className="mt-3 text-xs text-steel">
            {[item.localisation, item.date].filter(Boolean).join(" · ")}
          </p>
        )}
      </div>
    </motion.article>
  );
};

const Realisations = () => {
  const [filter, setFilter] = useState("all");

  // Les filtres ne s'affichent que pour les catégories réellement alimentées.
  const activeCategories = useMemo(
    () => CATEGORIES.filter((c) => REALISATIONS.some((r) => r.categories.includes(c.id))),
    []
  );

  const visible = useMemo(
    () =>
      filter === "all"
        ? REALISATIONS
        : REALISATIONS.filter((r) => r.categories.includes(filter)),
    [filter]
  );

  return (
    <>
      <Seo
        title="Réalisations fibre optique, raccordements et contrôle qualité | ATLAS THD"
        description="Découvrez les réalisations d'ATLAS THD : raccordements FTTO et FTTH, interventions terrain, inspection technique et contrôle qualité des infrastructures fibre optique."
        breadcrumbs={[{ label: "Accueil", to: "/" }, { label: "Réalisations" }]}
      />

      {/* HERO */}
      <section className="border-b border-line bg-cloud" data-testid="realisations-hero">
        <div className="bg-blueprint">
          <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:py-20 lg:px-8">
            <Breadcrumb items={[{ label: "Accueil", to: "/" }, { label: "Réalisations" }]} />
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mt-6 font-display text-xs font-bold uppercase tracking-[0.25em] text-steel"
            >
              Réalisations &amp; chantiers
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="mt-4 max-w-4xl font-display text-4xl font-extrabold tracking-tight text-midnight sm:text-5xl"
            >
              Nos réalisations terrain et nos missions qualité
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="mt-6 max-w-3xl text-lg leading-relaxed text-steel"
            >
              Découvrez les interventions et missions d'ATLAS THD dans le
              domaine des raccordements fibre optique, de l'inspection technique
              et du contrôle qualité des infrastructures télécoms.
            </motion.p>
          </div>
        </div>
      </section>

      {/* PORTFOLIO */}
      <section className="bg-white py-16 lg:py-24" data-testid="realisations-gallery">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {activeCategories.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="realisations-filters">
              <button
                onClick={() => setFilter("all")}
                data-testid="filter-all"
                className={`border px-4 py-2 text-xs font-semibold transition-colors duration-200 ${
                  filter === "all"
                    ? "border-midnight bg-midnight text-white"
                    : "border-line text-midnight hover:border-midnight"
                }`}
              >
                Toutes
              </button>
              {activeCategories.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setFilter(c.id)}
                  data-testid={`filter-${c.id}`}
                  className={`border px-4 py-2 text-xs font-semibold transition-colors duration-200 ${
                    filter === c.id
                      ? "border-midnight bg-midnight text-white"
                      : "border-line text-midnight hover:border-midnight"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          )}

          {visible.length > 0 ? (
            <div className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {visible.map((item, i) => (
                <RealisationCard key={item.id} item={item} index={i} />
              ))}
            </div>
          ) : (
            <motion.div
              {...fadeUp}
              transition={{ duration: 0.5 }}
              className="border border-line bg-cloud p-10 text-center sm:p-16"
              data-testid="realisations-empty-state"
            >
              <Camera className="mx-auto h-10 w-10 text-terrain" />
              <h2 className="mt-6 font-display text-2xl font-extrabold tracking-tight text-midnight">
                Portfolio en cours de publication
              </h2>
              <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-steel sm:text-base">
                Les photographies de nos chantiers et missions — raccordements
                FTTO et FTTH, interventions terrain, inspections techniques et
                audits qualité — sont en cours d'intégration. Chaque réalisation
                publiée ici est documentée à partir d'interventions réelles,
                avec l'accord de nos clients.
              </p>
              <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
                <Link
                  to="/terrain-fibre"
                  data-testid="empty-link-terrain"
                  className="inline-flex items-center justify-center gap-2 border border-midnight px-6 py-3 font-display text-sm font-bold uppercase tracking-wider text-midnight transition-colors hover:bg-midnight hover:text-white"
                >
                  Nos raccordements fibre
                </Link>
                <Link
                  to="/qualite-controle"
                  data-testid="empty-link-qualite"
                  className="inline-flex items-center justify-center gap-2 border border-midnight px-6 py-3 font-display text-sm font-bold uppercase tracking-wider text-midnight transition-colors hover:bg-midnight hover:text-white"
                >
                  Nos missions qualité
                </Link>
              </div>
            </motion.div>
          )}

          {/* CTA */}
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.5 }}
            className="mt-16 bg-midnight text-white"
            data-testid="realisations-cta"
          >
            <div className="bg-blueprint-dark flex flex-col items-start gap-8 p-8 sm:p-12 lg:flex-row lg:items-center lg:justify-between">
              <h2 className="max-w-2xl font-display text-2xl font-extrabold tracking-tight sm:text-3xl">
                Vous avez un chantier ou une mission à confier ? Contactez
                ATLAS THD.
              </h2>
              <div className="flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/contact"
                  data-testid="realisations-devis-button"
                  className="group flex items-center justify-center gap-2 bg-ciel px-6 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-midnight transition-colors duration-200 hover:bg-white"
                >
                  Parlez-nous de votre besoin
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
                <a
                  href="mailto:Contact.pro@atlasthd.com?subject=Présentation%20d'un%20projet"
                  data-testid="realisations-projet-button"
                  className="flex items-center justify-center gap-2 border border-white/30 px-6 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-white transition-colors duration-200 hover:border-white hover:bg-white hover:text-midnight"
                >
                  <Mail className="h-4 w-4" />
                  Présenter un projet
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Realisations;
